/**
 * Printed-mech state — the between-encounter attrition ledger and every way it is repaired,
 * reprinted, or restored.
 *
 * A leaf-handler group of `./leafReducer`'s switch; see `../reducer` for the tier's contract.
 */

import { CommandError, err, ok, type Result } from "@lancer-vtt/shared";
import { resolveActiveMechId } from "../activeMech";
import type { ApplyPrintedMechStateCommand, FullRepairCommand, FullRepairSquadCommand, ReprintCommand, RestoreDestroyedMechCommand, RestorePrintedMechStateCommand, RosterCommand, SpendRepairsCommand } from "../commands";
import { CORE_POWER_POOL, repairCost, replenishLimitedUsed, resolvePrintedMechValues, sparsePrintedMechState } from "../printedMech";
import type { PilotData, PrintedMechState, RosterState } from "../types";
import { changed, requireMech, requirePilot, withMech, withPilot, type RosterApplyResult, type RosterEvent } from "./context";

// ── printed-mech state (between-encounter attrition + repair) ────────────────────

/**
 * Set or delete a pilot's `printedMechs[mechId]` entry, returning the new pilot. A `null`
 * record **deletes** the key (restore-to-pristine); a record sets it. The single immutable
 * update the four printed-mech commands share.
 */
export function withPrintedMech(
  pilot: PilotData,
  mechId: string,
  record: PrintedMechState | null,
): PilotData {
  if (record === null) {
    const { [mechId]: _removed, ...rest } = pilot.printedMechs;
    return { ...pilot, printedMechs: rest };
  }
  return { ...pilot, printedMechs: { ...pilot.printedMechs, [mechId]: record } };
}

/** The inverse of any printed-mech mutation: restore the captured prior record (or its absence). */
/**
 * A Full Repair's mark on the Engineer's prototype (Core p.98, "each time you perform a Full Repair,
 * reroll ... this weapon's Limited uses"): a mech whose prototype carries a rolled Limited gets
 * `rerollDue`, so the MECH page asks for the re-roll. Returns the pilot unchanged and no inverse when
 * there is nothing to mark (no roll yet already reads as owed).
 */
export function markPrototypeRerollDue(
  pilot: PilotData,
  cmd: { actorId: string; pilotId: string; mechId: string },
): { pilot: PilotData; inverse?: RosterCommand } {
  const mech = pilot.mechs.find((m) => m.id === cmd.mechId);
  const prior = mech?.prototype;
  if (!mech || prior?.limitedUses === undefined || prior.rerollDue) return { pilot };
  return {
    pilot: withMech(pilot, { ...mech, prototype: { ...prior, rerollDue: true } }),
    inverse: { type: "set_mech_prototype", actorId: cmd.actorId, pilotId: cmd.pilotId, mechId: cmd.mechId, prototype: prior },
  };
}

export function printedMechInverse(
  cmd: { actorId: string; pilotId: string; mechId: string },
  prior: PrintedMechState | undefined,
): RosterCommand {
  return {
    type: "restore_printed_mech_state",
    actorId: cmd.actorId,
    pilotId: cmd.pilotId,
    mechId: cmd.mechId,
    state: prior ?? null,
  };
}

/**
 * Spend Repairs on one consequence track (Core p. 82). Draws the track's cost from the
 * mech's Repair Cap *remaining* (absent record ⇒ starts at `repairCapMax`); hard-rejects when
 * the cap can't cover the cost. Repairs HP to full / +1 Structure / +1 Stress (clamped to the
 * passed maxima), then stores a **sparse** record (pristine fields dropped; fully-pristine
 * deletes the key). The maxima ride the command (the reducer can't import `compute`).
 *
 * **`track: "item"`** is a fourth, non-numeric-track
 * branch: it hard-rejects a missing `itemKey` or one that isn't currently in the prior record's
 * `destroyedItems`, then removes just that key (leaving hp/structure/stress untouched) for the
 * same 1-Repair cost. `sparsePrintedMechState` only knows the four numeric tracks (shared with
 * the commit extractor), so `destroyedItems` is folded back in afterward — preserving it across
 * an ordinary hp/structure/stress spend too (it must never get silently dropped by an unrelated
 * repair, the same way `sparsePrintedMechState`'s numeric fields don't touch it).
 *
 * **`track: "limited"`** (`rest_limited_charge_replenish` — t_grease_monkey:1) is a fifth,
 * ALSO non-numeric-track branch, the `destroyedItems`/`"item"`
 * pattern one field over: hard-rejects when the prior record's `limitedUsed` AND
 * `limitedSystemUsed` (the Limited-SYSTEM half beside the weapons half) both have nothing consumed (a wasted
 * spend, mirroring `"item"`'s "not destroyed" reject), then replenishes 1 charge across
 * EVERY consumed entry in BOTH bags at once (`replenishLimitedUsed`, called once per bag —
 * no `itemKey`, RAW's "all Limited weapons and systems" is a single blanket spend, not two)
 * for the flat 2-Repair cost.
 */
export function applySpendRepairs(
  state: RosterState,
  cmd: SpendRepairsCommand,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  const mechR = requireMech(pilot, cmd.mechId);
  if (!mechR.ok) return mechR;

  const maxes = {
    maxHp: cmd.maxHp,
    maxStructure: cmd.maxStructure,
    maxStress: cmd.maxStress,
    repairCapMax: cmd.repairCapMax,
  };
  const prior = pilot.printedMechs[cmd.mechId];
  const cur = resolvePrintedMechValues(prior, maxes);

  if (cmd.track === "item") {
    if (!cmd.itemKey) {
      return err(new CommandError(`spend_repairs: track "item" requires an itemKey`));
    }
    if (!(prior?.destroyedItems ?? []).includes(cmd.itemKey)) {
      return err(
        new CommandError(
          `spend_repairs: item "${cmd.itemKey}" is not destroyed on mech ${cmd.mechId} — nothing to repair`,
        ),
      );
    }
  }
  if (
    cmd.track === "limited" &&
    !Object.values(prior?.limitedUsed ?? {}).some((n) => n > 0) &&
    !Object.entries(prior?.limitedSystemUsed ?? {}).some(([k, n]) => k !== CORE_POWER_POOL && n > 0)
  ) {
    return err(
      new CommandError(`spend_repairs: mech ${cmd.mechId} has no consumed Limited weapon/system charges — nothing to replenish`),
    );
  }

  const cost = repairCost(cmd.track, cmd.structureRepairCost);
  if (cur.repairsRemaining < cost) {
    return err(
      new CommandError(
        `spend_repairs: repairing ${cmd.track} costs ${cost} Repair(s) but only ${cur.repairsRemaining} of ${cmd.repairCapMax} remain`,
      ),
    );
  }

  const next = { ...cur, repairsRemaining: cur.repairsRemaining - cost };
  if (cmd.track === "hp") next.hp = maxes.maxHp;
  else if (cmd.track === "structure") next.structure = Math.min(cur.structure + 1, maxes.maxStructure);
  else if (cmd.track === "stress") next.stress = Math.min(cur.stress + 1, maxes.maxStress);
  // track === "item"/"limited": none of hp/structure/stress change; handled below.

  const record = sparsePrintedMechState(next, maxes);
  const destroyedItems =
    cmd.track === "item" ? (prior?.destroyedItems ?? []).filter((k) => k !== cmd.itemKey) : prior?.destroyedItems;
  const limitedUsed = cmd.track === "limited" ? replenishLimitedUsed(prior?.limitedUsed) : prior?.limitedUsed;
  const limitedSystemUsed =
    cmd.track === "limited" ? replenishLimitedUsed(prior?.limitedSystemUsed) : prior?.limitedSystemUsed;
  let merged: PrintedMechState | null = record;
  if (destroyedItems && destroyedItems.length > 0) merged = { ...(merged ?? {}), destroyedItems };
  if (limitedUsed && Object.keys(limitedUsed).length > 0) merged = { ...(merged ?? {}), limitedUsed };
  if (limitedSystemUsed && Object.keys(limitedSystemUsed).length > 0) merged = { ...(merged ?? {}), limitedSystemUsed };

  return ok({
    state: withPilot(state, withPrintedMech(pilot, cmd.mechId, merged)),
    inverse: printedMechInverse(cmd, prior),
    events: changed(cmd.pilotId),
  });
}

/**
 * Full Repair a printed mech: delete its `printedMechs` entry (back to pristine, Core p. 82).
 * Raw-only — no compendium, no maxima. Lossy delete, so the inverse captures the prior record.
 *
 * Hard-rejects a `destroyed: "vaporized"` record — the wreckage is
 * annihilated, nothing to repair (RAW p.81). A `"wreck"` record stays full-repairable (RAW
 * p.82: "as long as it's intact or the wreckage is accessible") — deletes the key as always.
 */
export function applyFullRepair(
  state: RosterState,
  cmd: FullRepairCommand,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  const mechR = requireMech(pilot, cmd.mechId);
  if (!mechR.ok) return mechR;

  const prior = pilot.printedMechs[cmd.mechId];
  if (prior?.destroyed === "vaporized") {
    return err(
      new CommandError(
        `full_repair: mech ${cmd.mechId} on pilot ${cmd.pilotId} was vaporized — wreckage not accessible, reprint required`,
      ),
    );
  }
  const marked = markPrototypeRerollDue(withPrintedMech(pilot, cmd.mechId, null), cmd);
  const printedInverse = printedMechInverse(cmd, prior);
  return ok({
    state: withPilot(state, marked.pilot),
    inverse: marked.inverse ? { type: "batch", commands: [printedInverse, marked.inverse] } : printedInverse,
    events: changed(cmd.pilotId),
  });
}

/**
 * One pilot's line in a squad Full Repair: what `full_repair_squad` will do to it. `mech` is
 * `"repair"` (the active mech carries wear, a wreck included), `"pristine"` (nothing to do),
 * `"vaporized"` (skipped: only a reprint clears it) or `"none"` (the pilot has no mech).
 * `pilotHp` is whether the pilot's carried on-foot HP is below full.
 */
export type SquadRepairEntry = {
  pilotId: string;
  callsign: string;
  mechId: string | null;
  mechName: string | null;
  mech: "repair" | "pristine" | "vaporized" | "none";
  pilotHp: boolean;
  /**
   * After this repair the mech's Engineer prototype owes its Limited re-roll (Core p.98): the repair
   * marks a rolled Limited `rerollDue` ({@link markPrototypeRerollDue}), or one was already due.
   */
  prototypeReroll: boolean;
};

/**
 * What a squad Full Repair of `pilotIds` will change, per pilot, in the given order. Unknown ids
 * are dropped (the reducer rejects them). The reducer applies exactly this plan, and the GM's
 * confirm lists it, so the two can't disagree.
 */
export function squadFullRepairPlan(state: RosterState, pilotIds: readonly string[]): SquadRepairEntry[] {
  return pilotIds.flatMap((pilotId) => {
    const pilot = state.pilots[pilotId];
    if (!pilot) return [];
    const mechId = resolveActiveMechId(pilot) ?? null;
    const mech = mechId === null ? undefined : pilot.mechs.find((m) => m.id === mechId);
    const printed = mechId === null ? undefined : pilot.printedMechs[mechId];
    const kind: SquadRepairEntry["mech"] = mechId === null ? "none" : printed === undefined ? "pristine" : printed.destroyed === "vaporized" ? "vaporized" : "repair";
    const proto = mech?.prototype;
    return [
      {
        pilotId,
        callsign: pilot.callsign,
        mechId,
        mechName: mech?.name ?? null,
        mech: kind,
        pilotHp: pilot.currentHp !== undefined,
        prototypeReroll: proto !== undefined && (proto.rerollDue === true || (kind === "repair" && proto.limitedUses !== undefined)),
      },
    ];
  });
}

/**
 * Full Repair the squad (`full_repair_squad`): every named pilot's active mech back to pristine
 * (skipping a vaporized one) and its carried HP back to full, as one command. The inverse is a
 * `batch` restoring each touched record and HP, so one undo puts the whole squad back.
 */
export function applyFullRepairSquad(
  state: RosterState,
  cmd: FullRepairSquadCommand,
): Result<RosterApplyResult, CommandError> {
  if (cmd.pilotIds.length === 0) return err(new CommandError("full_repair_squad: no pilots named"));
  for (const pilotId of cmd.pilotIds) {
    const pilotR = requirePilot(state, pilotId);
    if (!pilotR.ok) return pilotR;
  }
  const plan = squadFullRepairPlan(state, cmd.pilotIds);
  if (!plan.some((e) => e.mech === "repair" || e.pilotHp)) {
    return err(new CommandError("full_repair_squad: nothing to repair — every active mech is pristine and every pilot at full HP"));
  }

  let next = state;
  const inverses: RosterCommand[] = [];
  const events: RosterEvent[] = [];
  for (const entry of plan) {
    if (entry.mech !== "repair" && !entry.pilotHp) continue;
    let pilot = next.pilots[entry.pilotId]!;
    if (entry.mech === "repair" && entry.mechId !== null) {
      const ids = { actorId: cmd.actorId, pilotId: pilot.id, mechId: entry.mechId };
      inverses.push(printedMechInverse(ids, pilot.printedMechs[entry.mechId]));
      const marked = markPrototypeRerollDue(withPrintedMech(pilot, entry.mechId, null), ids);
      if (marked.inverse) inverses.push(marked.inverse);
      pilot = marked.pilot;
    }
    if (entry.pilotHp) {
      inverses.push({ type: "set_pilot_hp", actorId: cmd.actorId, pilotId: pilot.id, hp: pilot.currentHp });
      const { currentHp: _full, ...rest } = pilot;
      pilot = rest;
    }
    next = withPilot(next, pilot);
    events.push(...changed(pilot.id));
  }
  return ok({ state: next, inverse: { type: "batch", commands: inverses }, events });
}

/**
 * Commit a printed mech's post-fight state to the roster: store the already-sparse payload
 * (the caller-side extractor dropped pristine fields), deleting the key when the payload is
 * fully pristine. Trusted storage — no maxima, no clamping (the extractor built the payload
 * from a real, clamped combatant). Inverse captures the prior record.
 *
 * `cmd.destroyed` (set instead of the track fields when the combatant
 * ended destroyed) rides straight into the stored record.
 */
export function applyApplyPrintedMechState(
  state: RosterState,
  cmd: ApplyPrintedMechStateCommand,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  const mechR = requireMech(pilot, cmd.mechId);
  if (!mechR.ok) return mechR;

  const record: PrintedMechState = {};
  if (cmd.hp !== undefined) record.hp = cmd.hp;
  if (cmd.structure !== undefined) record.structure = cmd.structure;
  if (cmd.stress !== undefined) record.stress = cmd.stress;
  if (cmd.repairsRemaining !== undefined) record.repairsRemaining = cmd.repairsRemaining;
  if (cmd.destroyed !== undefined) record.destroyed = cmd.destroyed;
  if (cmd.ejectSpent !== undefined) record.ejectSpent = cmd.ejectSpent;
  if (cmd.destroyedItems !== undefined && cmd.destroyedItems.length > 0) record.destroyedItems = cmd.destroyedItems;
  if (cmd.limitedUsed !== undefined && Object.keys(cmd.limitedUsed).length > 0) record.limitedUsed = cmd.limitedUsed;
  if (cmd.limitedSystemUsed !== undefined && Object.keys(cmd.limitedSystemUsed).length > 0) {
    record.limitedSystemUsed = cmd.limitedSystemUsed;
  }
  const next: PrintedMechState | null = Object.keys(record).length === 0 ? null : record;

  const prior = pilot.printedMechs[cmd.mechId];
  return ok({
    state: withPilot(state, withPrintedMech(pilot, cmd.mechId, next)),
    inverse: printedMechInverse(cmd, prior),
    events: changed(cmd.pilotId),
  });
}

/**
 * Adjust just the `repairsRemaining` field of a (possibly absent) sparse record, leaving every
 * other field (`hp`/`structure`/`stress`/`destroyed`) untouched — the pooled restore's
 * per-contributor draw only ever touches this one field, never the contributor's own attrition
 * state, so it can't reuse `sparsePrintedMechState` (which needs the *full* current values +
 * maxima to re-sparsify all four track fields at once). Sparse convention preserved: dropping
 * back to the pristine cap deletes the field; an otherwise-empty record collapses to `null`.
 */
export function withRepairsRemaining(
  prior: PrintedMechState | undefined,
  repairsRemaining: number,
  repairCapMax: number,
): PrintedMechState | null {
  const next: PrintedMechState = { ...prior };
  if (repairsRemaining < repairCapMax) next.repairsRemaining = repairsRemaining;
  else delete next.repairsRemaining;
  return Object.keys(next).length === 0 ? null : next;
}

/**
 * **Restore a destroyed wreck** (Core p. 82's pooled 4-Repair restore).
 * Legal only when the target carries `destroyed: "wreck"`. Contributions must total exactly 4
 * Repairs; each is drawn from *that* contributor's own Repair Cap remaining (sequentially, so a
 * repeated pilot/mech pair in one command draws against its own already-reduced pool within
 * this command), with a hard reject on overdraw. Result is RAW-exact: the target's record
 * becomes `{structure: 1, stress: 1}` (HP absent ⇒ full, marker cleared). The inverse is a
 * `batch` of one `restore_printed_mech_state` per **distinct** pilot/mech touched (the target
 * plus every contributor), each capturing that key's state from *before* this command ran.
 */
export function applyRestoreDestroyedMech(
  state: RosterState,
  cmd: RestoreDestroyedMechCommand,
): Result<RosterApplyResult, CommandError> {
  const targetPilotR = requirePilot(state, cmd.pilotId);
  if (!targetPilotR.ok) return targetPilotR;
  const targetMechR = requireMech(targetPilotR.value, cmd.mechId);
  if (!targetMechR.ok) return targetMechR;

  const targetPrior = targetPilotR.value.printedMechs[cmd.mechId];
  if (targetPrior?.destroyed !== "wreck") {
    return err(
      new CommandError(
        `restore_destroyed_mech: mech ${cmd.mechId} on pilot ${cmd.pilotId} is not a destroyed wreck`,
      ),
    );
  }

  if (cmd.contributions.length === 0) {
    return err(new CommandError("restore_destroyed_mech: needs at least one contribution"));
  }
  if (cmd.contributions.some((c) => c.repairs <= 0)) {
    return err(new CommandError("restore_destroyed_mech: every contribution must pledge at least 1 Repair"));
  }
  const total = cmd.contributions.reduce((sum, c) => sum + c.repairs, 0);
  if (total !== 4) {
    return err(
      new CommandError(`restore_destroyed_mech: contributions must total exactly 4 Repairs (got ${total})`),
    );
  }

  let cur = state;
  const priorByKey = new Map<string, { pilotId: string; mechId: string; record: PrintedMechState | undefined }>();
  for (const c of cmd.contributions) {
    const cPilotR = requirePilot(cur, c.pilotId);
    if (!cPilotR.ok) return cPilotR;
    const cPilot = cPilotR.value;
    const cMechR = requireMech(cPilot, c.mechId);
    if (!cMechR.ok) return cMechR;

    const key = `${c.pilotId}:${c.mechId}`;
    if (!priorByKey.has(key)) {
      priorByKey.set(key, { pilotId: c.pilotId, mechId: c.mechId, record: cPilot.printedMechs[c.mechId] });
    }

    const record = cPilot.printedMechs[c.mechId];
    const remaining = record?.repairsRemaining ?? c.repairCapMax;
    if (remaining < c.repairs) {
      return err(
        new CommandError(
          `restore_destroyed_mech: contributor ${c.pilotId}/${c.mechId} has ${remaining} Repair(s) remaining but pledged ${c.repairs}`,
        ),
      );
    }
    const nextRecord = withRepairsRemaining(record, remaining - c.repairs, c.repairCapMax);
    cur = withPilot(cur, withPrintedMech(cPilot, c.mechId, nextRecord));
  }

  // Clear the target's destroyed marker last — RAW-exact 1 Structure / 1 Stress / full HP.
  const finalTargetPilotR = requirePilot(cur, cmd.pilotId);
  if (!finalTargetPilotR.ok) return finalTargetPilotR;
  cur = withPilot(cur, withPrintedMech(finalTargetPilotR.value, cmd.mechId, { structure: 1, stress: 1 }));

  const inverses: RosterCommand[] = [
    printedMechInverse({ actorId: cmd.actorId, pilotId: cmd.pilotId, mechId: cmd.mechId }, targetPrior),
    ...Array.from(priorByKey.values()).map((p) =>
      printedMechInverse({ actorId: cmd.actorId, pilotId: p.pilotId, mechId: p.mechId }, p.record),
    ),
  ];

  const affectedPilotIds = new Set<string>([cmd.pilotId, ...cmd.contributions.map((c) => c.pilotId)]);
  const events: RosterEvent[] = Array.from(affectedPilotIds).flatMap((id) => changed(id));

  return ok({
    state: cur,
    inverse: { type: "batch", commands: inverses },
    events,
  });
}

/**
 * **Reprint** a mech (the vaporized exit): delete its `printedMechs`
 * entry unconditionally — no gate, unlike `full_repair`. Raw-only, lossy delete; the inverse
 * captures the prior record.
 */
export function applyReprint(
  state: RosterState,
  cmd: ReprintCommand,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  const mechR = requireMech(pilot, cmd.mechId);
  if (!mechR.ok) return mechR;

  const prior = pilot.printedMechs[cmd.mechId];
  return ok({
    state: withPilot(state, withPrintedMech(pilot, cmd.mechId, null)),
    inverse: printedMechInverse(cmd, prior),
    events: changed(cmd.pilotId),
  });
}

/**
 * Internal inverse of the three printed-mech mutations: splice a captured prior record back
 * (or delete the key when `state` is `null` — restore to pristine). Its own inverse captures
 * the current record (or its absence), so undo↔redo round-trips exactly.
 */
export function applyRestorePrintedMechState(
  state: RosterState,
  cmd: RestorePrintedMechStateCommand,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  const mechR = requireMech(pilot, cmd.mechId);
  if (!mechR.ok) return mechR;

  const prior = pilot.printedMechs[cmd.mechId];
  return ok({
    state: withPilot(state, withPrintedMech(pilot, cmd.mechId, cmd.state)),
    inverse: printedMechInverse(cmd, prior),
    events: changed(cmd.pilotId),
  });
}
